import { AmplifyUser } from "@aws-amplify/ui";
import { errorToString } from "@metriport/shared";
import {
  createContext,
  Dispatch,
  ReactNode,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from "react";
import { setAuthTokenHeaders } from "../../../api/api";
import { getCustomers } from "../../../api/customer";
import { createCustomerAndRootMaps } from "../../../domain/customer";
import { getRoles } from "../../../shared/access-control";
import useMetriportToast from "../../shared/toast";
import { captureAndDisplayError, fetchUserToken } from "../../shared/util";
import { AppState, AppStateAction, AppStateActionType, initialState, reducer } from "./reducer";

interface IAppStateContext {
  state: AppState;
  dispatch: Dispatch<AppStateAction>;
}

export const AppStateContext = createContext<IAppStateContext>({
  state: initialState,
  dispatch: () => null,
});

export const AppStateProvider = ({
  user,
  children,
}: {
  user: AmplifyUser | undefined;
  children: ReactNode;
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const toast = useMetriportToast();
  const lastBootstrappedUserIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!user) {
      lastBootstrappedUserIdRef.current = undefined;
      return;
    }
    const userId =
      (user as { userId?: string }).userId ?? (user as { username?: string }).username ?? "";
    if (lastBootstrappedUserIdRef.current === userId) return;
    lastBootstrappedUserIdRef.current = userId;

    async function fetchInitialState() {
      try {
        const token = await fetchUserToken();
        setAuthTokenHeaders(token);

        if (!user) {
          throw new Error("Failed to fetch user");
        }

        const userRoles = getRoles(token);
        let initialCustomersState = {};
        if (userRoles.length > 0) {
          const customers = await getCustomers({ isRoot: true });
          initialCustomersState = createCustomerAndRootMaps(customers);
          initialCustomersState = {
            ...initialCustomersState,
            rawCustomers: customers,
          };
        }

        dispatch({
          type: AppStateActionType.update,
          newState: {
            authToken: token,
            user,
            userRoles,
            isLoaded: true,
            ...initialCustomersState,
          },
        });
      } catch (error) {
        captureAndDisplayError({
          error: errorToString(error),
          msg: "Failed to initialize the application",
          context: "fetchInitialState",
          captureMsg: "Component: AppStateProvider - initializing ops dash state failed",
          toast,
        });
        dispatch({
          type: AppStateActionType.update,
          newState: { isLoaded: true, userRoles: [] },
        });
      }
    }
    fetchInitialState();
  }, [user]);

  return (
    <AppStateContext.Provider value={{ state, dispatch }}>{children}</AppStateContext.Provider>
  );
};

export const useAppContext = () => useContext(AppStateContext);
